import React, { useState, useMemo } from 'react';
import {
  ClipboardCheck, Loader2, Search, Check, X, TrendingUp,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import {
  useGetDemandesRachatQuery,
  useApprouverDemandeMutation,
  useRejeterDemandeMutation,
  useApprouverLotMutation,
  useRejeterLotMutation,
} from '../../services/rachatApi';
import type { DemandeRachat } from '../../services/rachatApi';
import SimulationModal from './SimulationModal';

/** Group demandes by etudiant_matricule */
function groupByEtudiant(demandes: DemandeRachat[]) {
  const groups: Record<string, { nom: string; matricule: string; classe: string; demandes: DemandeRachat[] }> = {};
  for (const d of demandes) {
    if (!groups[d.etudiant_matricule]) {
      groups[d.etudiant_matricule] = {
        nom: d.etudiant_nom,
        matricule: d.etudiant_matricule,
        classe: d.etudiant_classe || '',
        demandes: [],
      };
    }
    groups[d.etudiant_matricule].demandes.push(d);
  }
  return Object.values(groups);
}

const ValidationDemandes: React.FC = () => {
  const [statutFilter, setStatutFilter] = useState('en_attente');
  const [searchEtudiant, setSearchEtudiant] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showSimulation, setShowSimulation] = useState(false);
  const [expandedMatricule, setExpandedMatricule] = useState<string | null>(null);

  const queryParams: Record<string, string> = {};
  if (statutFilter) queryParams.statut = statutFilter;
  const { data: rawData, isLoading, refetch } = useGetDemandesRachatQuery(queryParams);
  const demandes: DemandeRachat[] = Array.isArray(rawData) ? rawData : (rawData as any)?.results ?? [];

  const [approuverDemande] = useApprouverDemandeMutation();
  const [rejeterDemande] = useRejeterDemandeMutation();
  const [approuverLot, { isLoading: approvingLot }] = useApprouverLotMutation();
  const [rejeterLot, { isLoading: rejectingLot }] = useRejeterLotMutation();

  const filtered = useMemo(() => {
    if (!searchEtudiant) return demandes;
    const q = searchEtudiant.toLowerCase();
    return demandes.filter(d =>
      d.etudiant_matricule?.toLowerCase().includes(q) ||
      d.etudiant_nom?.toLowerCase().includes(q)
    );
  }, [demandes, searchEtudiant]);

  const groups = useMemo(() => groupByEtudiant(filtered), [filtered]);

  const stats = useMemo(() => ({
    en_attente: demandes.filter(d => d.statut === 'en_attente').length,
    approuvees: demandes.filter(d => d.statut === 'approuve').length,
    rejetees: demandes.filter(d => d.statut === 'rejete').length,
  }), [demandes]);

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelectedIds(filtered.filter(d => d.statut === 'en_attente').map(d => d.id));
  };

  const handleApprouverLot = async () => {
    if (selectedIds.length === 0) return;
    await approuverLot({ demande_ids: selectedIds });
    setSelectedIds([]);
  };

  const handleRejeterLot = async () => {
    if (selectedIds.length === 0) return;
    await rejeterLot({ demande_ids: selectedIds });
    setSelectedIds([]);
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'En attente', value: stats.en_attente, color: 'orange' },
          { label: 'Approuvées', value: stats.approuvees, color: 'green' },
          { label: 'Rejetées', value: stats.rejetees, color: 'red' },
        ].map(s => (
          <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="text-xs text-gray-500 dark:text-gray-400">{s.label}</div>
            <div className={`text-2xl font-bold text-${s.color}-600 dark:text-${s.color}-400`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Statut</label>
            <select
              value={statutFilter}
              onChange={e => { setStatutFilter(e.target.value); setSelectedIds([]); }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">Tous</option>
              <option value="en_attente">En attente</option>
              <option value="approuve">Approuvées</option>
              <option value="rejete">Rejetées</option>
              <option value="annule">Annulées</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rechercher étudiant</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchEtudiant}
                onChange={e => setSearchEtudiant(e.target.value)}
                placeholder="Matricule, nom..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div className="flex items-end">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filtered.length} demande{filtered.length > 1 ? 's' : ''} — {groups.length} étudiant{groups.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-3 flex items-center justify-between">
          <span className="text-sm text-blue-800 dark:text-blue-200 font-medium">
            {selectedIds.length} demande{selectedIds.length > 1 ? 's' : ''} sélectionnée{selectedIds.length > 1 ? 's' : ''}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setShowSimulation(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <TrendingUp size={14} />
              Simuler
            </button>
            <button
              onClick={handleApprouverLot}
              disabled={approvingLot}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              <Check size={14} />
              {approvingLot ? 'Approbation...' : 'Approuver'}
            </button>
            <button
              onClick={handleRejeterLot}
              disabled={rejectingLot}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
            >
              <X size={14} />
              {rejectingLot ? 'Rejet...' : 'Rejeter'}
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Content — Grouped by student */}
      {isLoading ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-blue-500 mr-3" />
          <span className="text-gray-500 dark:text-gray-400">Chargement...</span>
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-center py-16">
          <ClipboardCheck size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 text-sm">Aucune demande de rachat trouvée.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {statutFilter === 'en_attente' && filtered.some(d => d.statut === 'en_attente') && (
            <div className="flex items-center gap-3 px-4">
              <input
                type="checkbox"
                checked={selectedIds.length > 0 && selectedIds.length === filtered.filter(d => d.statut === 'en_attente').length}
                onChange={e => e.target.checked ? selectAll() : setSelectedIds([])}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">Tout sélectionner</span>
            </div>
          )}

          {groups.map(group => {
            const isExpanded = expandedMatricule === group.matricule;
            const enAttente = group.demandes.filter(d => d.statut === 'en_attente');

            return (
              <div key={group.matricule} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setExpandedMatricule(isExpanded ? null : group.matricule)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{group.nom}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{group.matricule}</span>
                    {group.classe && <span className="text-xs text-gray-400">({group.classe})</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {group.demandes.length} demande{group.demandes.length > 1 ? 's' : ''}
                    </span>
                    {enAttente.length > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 font-semibold">
                        {enAttente.length} en attente
                      </span>
                    )}
                    {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                          <th className="px-4 py-2 w-10"></th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">UE</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Matière</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Note Exam Avant</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Note Exam Après</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Statut</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Date</th>
                          <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {group.demandes.map(d => (
                          <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                            <td className="px-4 py-2">
                              {d.statut === 'en_attente' && (
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(d.id)}
                                  onChange={() => toggleSelect(d.id)}
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                />
                              )}
                            </td>
                            <td className="px-4 py-2 font-mono text-xs text-gray-500 dark:text-gray-400">
                              {d.ue_code || '—'}
                            </td>
                            <td className="px-4 py-2">
                              <div className="text-gray-800 dark:text-gray-200">{d.matiere_nom}</div>
                              <div className="text-xs text-gray-400">{d.matiere_code}</div>
                            </td>
                            <td className="px-4 py-2 text-center font-mono text-gray-600 dark:text-gray-300">
                              {parseFloat(d.note_originale).toFixed(2)}
                            </td>
                            <td className="px-4 py-2 text-center font-mono font-semibold text-green-600 dark:text-green-400">
                              {parseFloat(d.note_proposee).toFixed(2)}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                                d.statut === 'en_attente' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                                d.statut === 'approuve' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                                d.statut === 'rejete' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                                {d.statut === 'en_attente' ? 'En attente' :
                                 d.statut === 'approuve' ? 'Approuvée' :
                                 d.statut === 'rejete' ? 'Rejetée' : 'Annulée'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(d.date_demande)}
                            </td>
                            <td className="px-4 py-2 text-center">
                              {d.statut === 'en_attente' && (
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => approuverDemande(d.id)}
                                    title="Approuver"
                                    className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400"
                                  >
                                    <Check size={14} />
                                  </button>
                                  <button
                                    onClick={() => rejeterDemande(d.id)}
                                    title="Rejeter"
                                    className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Simulation Modal */}
      {showSimulation && selectedIds.length > 0 && (
        <SimulationModal
          demandeIds={selectedIds}
          onClose={() => setShowSimulation(false)}
          onApproved={() => {
            setShowSimulation(false);
            setSelectedIds([]);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default ValidationDemandes;
